import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';

interface Dress {
    id: number;
    attributes: {
        Name: string;
        Type: string;
        deliveryDate: string;
        startDate: string;
        finishDate: string;
        notes?: string;
        measurements?: string;
        sketches?: {
            data: any[];
        };
        fabricPhotos?: {
            data: any[];
        };
        audioVideoNotes?: {
            data: any[];
        };
        createdAt: string;
        updatedAt: string;
    };
}

interface DressesState {
    dresses: Dress[];
    singleDress: Dress | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: DressesState = {
    dresses: [],
    singleDress: null,
    isLoading: false,
    error: null
};

// Fetch a single dress by ID
export const fetchDress = createAsyncThunk(
    'dresses/fetchDress',
    async (dressId: number, { rejectWithValue }) => {
        try {
            const response = await api.get(`/api/dresses/${dressId}?populate=*`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch dress details');
        }
    }
);

// Create a new dress
export const createDress = createAsyncThunk(
    'dresses/createDress',
    async (dressData: any, { rejectWithValue }) => {
        try {
            const response = await api.post('/api/dresses', { data: dressData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create dress');
        }
    }
);

// Update an existing dress
export const updateDress = createAsyncThunk(
    'dresses/updateDress',
    async ({ id, dressData }: { id: number; dressData: any }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/api/dresses/${id}`, { data: dressData });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update dress');
        }
    }
);

// Delete a dress
export const deleteDress = createAsyncThunk(
    'dresses/deleteDress',
    async (dressId: number, { rejectWithValue }) => {
        try {
            await api.delete(`/api/dresses/${dressId}`);
            return dressId;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete dress');
        }
    }
);

const dressesSlice = createSlice({
    name: 'dresses',
    initialState,
    reducers: {
        clearDressErrors: (state) => {
            state.error = null;
        },
        clearSingleDress: (state) => {
            state.singleDress = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch dress
            .addCase(fetchDress.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDress.fulfilled, (state, action: PayloadAction<{ data: Dress }>) => {
                state.isLoading = false;
                state.singleDress = action.payload.data;
            })
            .addCase(fetchDress.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string || 'Failed to fetch dress';
            })
            
            // Create dress
            .addCase(createDress.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createDress.fulfilled, (state, action: PayloadAction<{ data: Dress }>) => {
                state.isLoading = false;
                state.dresses.push(action.payload.data);
            })
            .addCase(createDress.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string || 'Failed to create dress';
            })
            
            // Update dress
            .addCase(updateDress.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateDress.fulfilled, (state, action: PayloadAction<{ data: Dress }>) => {
                state.isLoading = false;
                if (state.singleDress && state.singleDress.id === action.payload.data.id) {
                    state.singleDress = action.payload.data;
                }
                state.dresses = state.dresses.map(dress => 
                    dress.id === action.payload.data.id ? action.payload.data : dress
                );
            })
            .addCase(updateDress.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string || 'Failed to update dress';
            })
            
            // Delete dress
            .addCase(deleteDress.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteDress.fulfilled, (state, action: PayloadAction<number>) => {
                state.isLoading = false;
                state.dresses = state.dresses.filter(dress => dress.id !== action.payload);
                if (state.singleDress && state.singleDress.id === action.payload) {
                    state.singleDress = null;
                }
            })
            .addCase(deleteDress.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string || 'Failed to delete dress';
            });
    }
});

export const { clearDressErrors, clearSingleDress } = dressesSlice.actions;
export default dressesSlice.reducer; 